import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { orderBy, uniq } from 'lodash';
import TableRequested from './TableRequested';
import Loader from '../../general/Loader';
import pool from '@ricokahler/pool';

import { currentUserOperations, currentUserSelectors } from '../../../state/modules/current-user';
import { operations as userOperations, selectors as userSelectors } from '../../../state/modules/users';
import { operations as groupOperations, selectors as groupSelectors } from '../../../state/modules/groups';
import {
  operations as dutyTransactionOperations,
  dutyTransactionsSelectors
} from '../../../state/modules/duty-transactions';

import { operations as contractStatisticsOperations } from '../../../state/modules/contract-statistics';

// Component for displaying the status of candidates of an extra shift request,
// including the list of duty exchange intervals and the contract statistics for
// each user. Allows filtering and sorting the duty exchange intervals based on
// the evaluation of a selected constraint.
const DutyTransactionCandidateStatus = ({
  augmentedTransactionsById,
  canRespondOnBehalf,
  currentUserId,
  dutyTransactionId,
  fetchContractStatistics,
  fetchDutyTransactions,
  fetchGroups,
  fetchUsers,
  getCurrentUser,
  initialLoadComplete
}) => {
  const timeUnit = 'shift';
  const [sortByConstraint, setSortByConstraint] = useState(1);
  const [selectedConstraint, setSelectedConstraint] = useState(null);

  useEffect(() => {
    fetchDutyTransactions({ ids: dutyTransactionId, onlyMissing: true });
  }, [fetchDutyTransactions, dutyTransactionId]);

  const dutyTransaction = useMemo(
    () => augmentedTransactionsById[dutyTransactionId],
    [augmentedTransactionsById, dutyTransactionId]
  );

  const dutyExchangeIntervals = useMemo(() => dutyTransaction?.duty_exchange_intervals || [], [dutyTransaction]);
  // get the list of unique user ids from the duty exchange intervals. We use a
  // string here to avoid having to deal with array equality in the dependency
  // array of the useEffect that fetches the users
  const userIdString = dutyExchangeIntervals.map(interval => interval.user_id).join(',');

  const userIds = useMemo(() => uniq(userIdString.split(',').filter(Boolean)), [userIdString]);

  // fetch the users for all duty exchange intervals, so that we can display
  // their names
  useEffect(() => {
    fetchUsers({ ids: userIds, onlyMissing: true });
  }, [fetchUsers, userIds]);

  const groupIds = useMemo(() => {
    const users = dutyExchangeIntervals.map(interval => interval.user) || [];
    const groupIds = users
      .flatMap(u => u?.memberships || [])
      .map(m => m?.group_id)
      .filter(Boolean);
    return uniq(groupIds).sort();
  }, [dutyExchangeIntervals]);

  // fetch the groups for all users involved in the duty exchange intervals, so
  // that we can display the station of each user in the table
  useEffect(() => {
    fetchGroups({ ids: groupIds, onlyMissing: true, params: { group_type: 'station' } });
  }, [fetchGroups, groupIds.join(',')]);

  useEffect(() => {
    getCurrentUser();
  }, [getCurrentUser]);

  useEffect(() => {
    pool({
      collection: userIds,
      maxConcurrency: 3,
      task: userId => fetchContractStatistics({ ids: userId, params: { time_unit: timeUnit } })
    });
  }, [fetchContractStatistics, userIds]);

  // get the list of unique constraints from all contract statistics
  const constraints = useMemo(
    () =>
      uniq(
        dutyExchangeIntervals
          .flatMap(interval => interval.contractStatistics || [])
          .flatMap(cs => cs.constraint_evaluation || [])
          .map(ce => ce.name)
      ).sort(),
    [dutyExchangeIntervals]
  );

  // if a constraint is selected, filter the contract statistics to only include
  // those that have an evaluation for the selected constraint
  const dutyExchangeIntervalsWithFilteredStats = useMemo(() => {
    let result = orderBy(dutyExchangeIntervals, 'state');

    if (!selectedConstraint) return result;

    result = result
      .map(interval => ({
        ...interval,
        contractStatistics: interval.contractStatistics.filter(cs =>
          (cs.constraint_evaluation || []).find(evaluation => evaluation.name === selectedConstraint)
        )
      }))
      .filter(interval => interval.contractStatistics.length > 0);

    result = orderBy(result, interval => {
      const evaluation = interval.contractStatistics
        .flatMap(c => c.constraint_evaluation || [])
        .find(evaluation => evaluation.name === selectedConstraint);
      return evaluation.value;
    });

    if (sortByConstraint === -1) result.reverse();

    return result;
  }, [dutyExchangeIntervals, selectedConstraint, sortByConstraint]);

  if (!dutyTransaction || !initialLoadComplete) return <Loader />;

  const reverseSort = () => setSortByConstraint(-sortByConstraint);

  const state = dutyTransaction.state;

  return (
    <TableRequested
      canRespondOnBehalf={canRespondOnBehalf}
      constraints={constraints}
      currentUserId={currentUserId}
      groupId={dutyTransaction.group_id}
      setSelectedConstraint={setSelectedConstraint}
      intervals={dutyExchangeIntervalsWithFilteredStats}
      reverseSort={reverseSort}
      selectedConstraint={selectedConstraint}
      sortByConstraint={sortByConstraint}
      mayResolve={canRespondOnBehalf && (state == 'resolvable' || state == 'pending_approval')}
    />
  );
};

const mapStateToProps = state => ({
  currentUserId: currentUserSelectors.selectCurrentUserId(state),
  initialLoadComplete: userSelectors.initialLoadComplete(state) && groupSelectors.initialLoadComplete(state),
  augmentedTransactionsById: dutyTransactionsSelectors.augmentedTransactionsById(state)
});

const mapDispatchToProps = {
  getCurrentUser: currentUserOperations.getCurrentUser,
  fetchDutyTransactions: dutyTransactionOperations.fetch,
  fetchUsers: userOperations.fetch,
  fetchGroups: groupOperations.fetch,
  fetchContractStatistics: contractStatisticsOperations.fetch
};

export default connect(mapStateToProps, mapDispatchToProps)(DutyTransactionCandidateStatus);
